/* eslint-disable react/prop-types */
import React, { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Chart } from 'chart.js'
import { selectReport } from '../../../../redux/selectors'

export default function ListingGraph({
  reportId, title, formatTimestampLabels,
}) {
  const reportCanvasRef = useRef(null)
  const chartRef = useRef(null)
  const report = useSelector(selectReport(reportId))

  // Destroy the chart only when the component unmounts. Data changes are
  // applied in place below so a cached-then-refetched report does not
  // tear down and re-animate the chart (a single grow, not grow-reset-grow).
  useEffect(() => () => {
    if (chartRef.current) {
      chartRef.current.destroy()
      chartRef.current = null
    }
  }, [])

  useEffect(() => {
    const canvas = reportCanvasRef.current
    if (!canvas) {
      return
    }
    const context = canvas.getContext('2d')
    const rawLines = report && report.data && Array.isArray(report.data.lines) ? report.data.lines : null
    const lines = rawLines
      ? [...rawLines].sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp))
      : null

    // No usable data: tear down any existing chart and show a message.
    if (!report || !rawLines || lines.length === 0) {
      if (chartRef.current) {
        chartRef.current.destroy()
        chartRef.current = null
      }
      if (report) {
        canvas.width = '350'
        context.font = '18px helvetica'
        context.textBaseline = 'middle'
        context.textAlign = 'center'
        const message = !rawLines
          ? 'Unable to load report data'
          : `There is no data for${title === 'Today' ? ' ' : ' the '}${title.toLowerCase()}`
        context.fillText(message, canvas.width / 2, canvas.height / 2)
      }
      return
    }

    const labels = formatTimestampLabels(lines, reportId)
    const data = lines.map((x) => x.total)

    // Update in place when a chart already exists to avoid re-animating.
    if (chartRef.current) {
      chartRef.current.data.labels = labels
      chartRef.current.data.datasets[0].data = data
      chartRef.current.update()
      return
    }

    chartRef.current = new Chart(context, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Authorizations',
            data,
            fill: false,
            backgroundColor: 'rgba(46,204,113,.5)',
          },
        ],
      },
      options: {
        title: {
          display: false,
          text: title,
          fontSize: 18,
          fontFamily: 'helvetica',
          fontStyle: 'normal',
        },
        layout: {
          padding: {
            bottom: 20,
          },
        },
        scales: {
          xAxes: [{ stacked: true, gridLines: { display: false } }],
          yAxes: [{ stacked: true }],
        },
        legend: {
          display: false,
        },
        tooltips: {
          mode: 'index',
          intersect: false,
        },
      },
    })
  }, [report])

  return (
    <div style={{ marginBottom: '50px', textAlign: 'center', overflow: 'hidden' }}>
      {report ? null : <div>Loading...</div>}
      <canvas height="300" width="800" ref={reportCanvasRef} />
    </div>
  )
}
